<template>
    <div :id="drawerId"
         class="app-drawer custom-scrollbar"
         :class="[`app-drawer--${drawerPosition}`, { 'app-drawer--visible': isVisible }]"
         :data-overlay-open="isOverlayOpen ? 'true' : 'false'"
         :style="drawerRootStyle"
         tabindex="-1"
         role="dialog"
         aria-modal="true"
         :aria-hidden="`${!isVisible}`">
        <div class="app-drawer__backdrop"
             :style="'cursor: url('+cursorImage()+'), auto !important'"
             @click="handleBackdropClick"></div>

        <div ref="panel"
             class="app-drawer__panel"
             :class="drawerPanelClass"
             :style="drawerPanelStyle">
            <div v-if="$slots.header" class="app-drawer__header" :class="drawerHeaderClass">
                <slot name="header"></slot>
            </div>

            <div v-if="$slots.body" class="app-drawer__body custom-scrollbar" :class="drawerBodyClass">
                <slot name="body"></slot>
            </div>

            <div v-if="$slots.footer" class="app-drawer__footer" :class="drawerFooterClass">
                <slot name="footer"></slot>
            </div>

            <slot></slot>
        </div>
    </div>
</template>

<script>
import AppFunction from "../../helpers/app/AppFunction";
import OverlayStack from "../../helpers/app/OverlayStack";

const DRAWER_TRANSITION_MS = 320;

export default {
    name: "AppDrawer",
    props: {
        drawerId: {
            type: String,
            required: true
        },
        drawerWidth: {
            type: String,
            default: '32rem'
        },
        drawerPosition: {
            type: String,
            default: 'right'
        },
        drawerBodyClass: {
            type: String,
            default: ''
        },
        drawerHeaderClass: {
            type: String,
            default: ''
        },
        drawerFooterClass: {
            type: String,
            default: ''
        },
        drawerPanelClass: {
            type: String,
            default: ''
        },
        drawerBackdrop: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isVisible: false,
            isOverlayOpen: false,
            overlayZIndex: 1050,
            closeTimeout: null,
            isClosing: false,
        };
    },
    computed: {
        drawerRootStyle() {
            return {
                zIndex: this.overlayZIndex
            };
        },
        drawerPanelStyle() {
            return {
                '--app-drawer-width': this.drawerWidth
            };
        }
    },
    mounted() {
        if (this.$el.parentNode !== document.body) {
            document.body.appendChild(this.$el);
        }

        this.overlayZIndex = OverlayStack.nextZIndex(this.drawerId);
        this.setOverlayState(true);
        this.bindEvents();
        OverlayStack.syncBodyState();

        this.$nextTick(() => {
            if (typeof this.$el.focus === 'function') {
                this.$el.focus();
            }

            window.requestAnimationFrame(() => {
                this.isVisible = true;
                this.$emit('opened');
            });
        });
    },
    beforeDestroy() {
        this.unbindEvents();
        window.clearTimeout(this.closeTimeout);
        this.setOverlayState(false);
        this.isVisible = false;
        OverlayStack.syncBodyState();
    },
    methods: {
        setOverlayState(isOpen) {
            this.isOverlayOpen = isOpen;

            if (this.$el) {
                this.$el.setAttribute('data-overlay-open', isOpen ? 'true' : 'false');
            }
        },
        requestClose() {
            if (this.isClosing) {
                return;
            }

            this.isClosing = true;
            this.isVisible = false;
            this.closeTimeout = window.setTimeout(() => {
                this.setOverlayState(false);
                OverlayStack.syncBodyState();
                this.$emit('close-modal', false);
            }, DRAWER_TRANSITION_MS);
        },
        handleBackdropClick() {
            if (!this.drawerBackdrop) {
                return;
            }

            this.requestClose();
        },
        handleKeydown(event) {
            if (event.key === 'Escape' && this.drawerBackdrop) {
                this.requestClose();
            }
        },
        bindEvents() {
            document.addEventListener('keydown', this.handleKeydown);
        },
        unbindEvents() {
            document.removeEventListener('keydown', this.handleKeydown);
        },
        cursorImage() {
            if (this.$store.state.theme.darkMode) {
                return AppFunction.getAppUrl('images/close-cursor-dark.png');
            }

            return AppFunction.getAppUrl('images/close-cursor-light.png');
        }
    }
}
</script>

<style scoped>
.app-drawer {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: stretch;
    justify-content: flex-end;
    overflow: hidden;
    pointer-events: none;
}

.app-drawer--left {
    justify-content: flex-start;
}

.app-drawer__backdrop {
    position: absolute;
    inset: 0;
    opacity: 0;
    background-color: rgba(7, 16, 27, 0.88);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    transition: opacity 220ms ease;
    pointer-events: auto;
}

.app-drawer__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    width: var(--app-drawer-width, 32rem);
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    margin-left: auto;
    overflow: hidden;
    background-color: var(--modal-header-footer-bg);
    box-shadow: none;
    transform: translate3d(100%, 0, 0);
    transition: transform 320ms cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: auto;
}

.app-drawer--left .app-drawer__panel {
    margin-right: auto;
    margin-left: 0;
    transform: translate3d(-100%, 0, 0);
}

.app-drawer--visible .app-drawer__backdrop {
    opacity: 1;
}

.app-drawer--visible .app-drawer__panel {
    transform: translate3d(0, 0, 0);
}

.app-drawer__header,
.app-drawer__footer {
    display: flex;
    flex: 0 0 auto;
}

.app-drawer__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

@media (max-width: 991px) {
    .app-drawer__panel {
        width: min(var(--app-drawer-width, 100vw), 100vw);
    }
}
</style>
